import React, { PureComponent } from "react";
import { parseThreshold, ThresholdUnits } from "./Threshold";

/**
 * Determines if a scrollable element has reached its bottom
 * @param {HTMLElement} target - Element to check
 * @param {number|string} scrollThreshold - Threshold value (0.8 = 80%)
 * @returns {boolean} True if the element is at bottom
 */
function isElementAtBottom(target, scrollThreshold = 0.8) {
  // Handle case where target might not be available
  if (!target) return false;

  const clientHeight =
    target === document.body || target === document.documentElement
      ? window.screen.availHeight
      : target.clientHeight;

  const scrollHeight = target.scrollHeight || 0;
  const scrollTop = target.scrollTop || 0;
  
  // If element has no scroll height, it's not scrollable
  if (scrollHeight <= clientHeight) return true;
  
  const threshold = parseThreshold(scrollThreshold);

  if (threshold.unit === ThresholdUnits.Pixel) {
    return scrollTop + clientHeight >= scrollHeight - threshold.value;
  }

  return scrollTop + clientHeight >= (threshold.value / 100) * scrollHeight;
}

/**
 * Component for infinite scrolling functionality
 */
class InfiniteScroller extends PureComponent {
  componentDidMount() {
    this.attachScrollListener();
  }
  
  componentDidUpdate(prevProps) {
    // Re-attach listener if parentId changes
    if (prevProps.parentId !== this.props.parentId) {
      this.detachScrollListener();
      this.attachScrollListener();
    }
    
    // Backward compatibility: Check if we should load more when children change
    // This supports scenarios where the content height changes after render
    if (prevProps.children !== this.props.children) {
      setTimeout(this.checkIfShouldLoadMore, 100);
    }
  }

  componentWillUnmount() {
    this.detachScrollListener();
  }
  
  /**
   * Attach scroll event listener
   */
  attachScrollListener = () => {
    const { parentId = "main-layout" } = this.props;
    const containerEl = document.getElementById(parentId);
    
    if (containerEl) {
      containerEl.addEventListener("scroll", this.handleScroll);
      
      // Check if we should load more content right away
      // (if the content doesn't fill the container)
      this.checkIfShouldLoadMore();
    }
  };
  
  /**
   * Detach scroll event listener
   */
  detachScrollListener = () => {
    const { parentId = "main-layout" } = this.props;
    const containerEl = document.getElementById(parentId);
    
    if (containerEl) {
      containerEl.removeEventListener("scroll", this.handleScroll);
    }
  };
  
  /**
   * Check if we need to load more content right away
   */
  checkIfShouldLoadMore = () => {
    const { loadMore, hasMore = true, parentId = "main-layout" } = this.props;
    const containerEl = document.getElementById(parentId);
    
    if (containerEl && typeof loadMore === 'function') {
      if (isElementAtBottom(containerEl)) {
        // For backward compatibility: call loadMore even if hasMore isn't provided
        loadMore();
      }
    }
  };

  /**
   * Handle scroll event
   * @param {Event} e - Scroll event
   */
  handleScroll = (e) => {
    // Don't prevent default scroll behavior - this was causing the "not allowed" error
    // The preventDefault was incorrectly blocking normal scrolling
    
    const { loadMore, hasMore = true, parentId = "main-layout" } = this.props;
    const containerEl = document.getElementById(parentId);
    
    if (containerEl && typeof loadMore === 'function') {
      if (isElementAtBottom(containerEl)) {
        // For backward compatibility: call loadMore even if hasMore isn't provided
        loadMore();
      }
    }
  };

  render() {
    // For backward compatibility, use the same div structure as before
    return <div>{this.props.children}</div>;
  }
}

export default InfiniteScroller;
